// src/utils/api.ts
import axios from 'axios';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './tokenStore';

// Smart API URL detection based on current domain
const getApiUrl = () => {
  // Allow override via env var (used for staging builds)
  if (process.env.REACT_APP_API_URL !== undefined) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV !== 'production') {
    return ''; // Use proxy in development
  }
  return 'https://ascension-ai-backend-hnhtbah0dwahcud3.eastus2-01.azurewebsites.net';
};

const API_URL = getApiUrl();

// Default timeout for JSON requests. File uploads (FormData) get a longer
// budget — Azure Web Apps caps requests at 230s, so 5 minutes is a safe ceiling.
export const DEFAULT_TIMEOUT_MS = 30000;
export const UPLOAD_TIMEOUT_MS = 300000;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache'
  },
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
});

// Multipart/file uploads need a much longer client-side timeout than the 30s
// default — they upload bytes, then the server merges PDFs, writes to SQL, and
// uploads to blob storage before responding. The 30s default was aborting IVR
// submissions with chart documents, leaving users to retry and unknowingly
// create duplicate submissions.
api.interceptors.request.use((config) => {
  const isFormData =
    typeof FormData !== 'undefined' && config.data instanceof FormData;
  if (isFormData && config.timeout === DEFAULT_TIMEOUT_MS) {
    config.timeout = UPLOAD_TIMEOUT_MS;
  }

  // Bearer fallback for browsers that drop the third-party auth cookie.
  // `withCredentials` stays on, so the cookie remains the primary credential
  // wherever it works (the backend reads the cookie first, then this header) —
  // this only rescues cookie-blocked browsers. We never overwrite an
  // Authorization header the caller set explicitly (e.g. the MFA setup-token
  // flow passes its own Bearer).
  const headers = config.headers as Record<string, unknown> | undefined;
  const callerSetAuth = !!(headers && (headers.Authorization || headers.authorization));
  const accessToken = getAccessToken();
  if (accessToken && !callerSetAuth) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Track refresh state to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let isRedirecting = false;

// Stamp the current stored access token onto a request being retried after a
// refresh. Needed because the retried request already carries the stale header
// from its first attempt, which the request interceptor will not overwrite.
function applyBearer(request: any): void {
  const accessToken = getAccessToken();
  if (request && accessToken) {
    request.headers = request.headers || {};
    request.headers.Authorization = `Bearer ${accessToken}`;
  }
}

// Add response interceptor with token refresh logic
api.interceptors.response.use(
  (response) => {
    // Centralized token lifecycle for the Bearer fallback so individual
    // callers (Login, MFAVerification, the 8 logout sites) need no changes.
    const url = response.config?.url || '';
    if (url.includes('/api/logout')) {
      clearTokens();
    } else if (url.includes('/api/login') && response.data) {
      // Only the FINAL auth success carries token+refreshToken; the interim
      // requiresMFA / requiresMFASetup responses have neither, so this no-ops
      // for them.
      const { token, refreshToken } = response.data as { token?: string; refreshToken?: string };
      if (token || refreshToken) setTokens({ token, refreshToken });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Logout must drop the stored tokens even when the request itself fails
    // (network/5xx) — otherwise a cookie-blocked browser keeps a valid Bearer
    // token in storage and the next /api/users/me silently re-resumes the
    // session the user tried to end.
    if (originalRequest?.url?.includes('/api/logout')) {
      clearTokens();
    }

    // If 401 and not already retried and not the refresh/login endpoint itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/refresh') &&
      !originalRequest.url?.includes('/api/login') &&
      !originalRequest.url?.includes('/api/logout')
    ) {
      originalRequest._retry = true;

      // If already refreshing, wait for the existing refresh to complete
      if (isRefreshing && refreshPromise) {
        try {
          const shared = await refreshPromise;
          // Persist tokens from the shared refresh response here too, so this
          // branch doesn't depend on the primary branch's continuation running
          // first. setTokens is idempotent.
          const sharedData = (shared && shared.data) || {};
          if (sharedData.token || sharedData.refreshToken) {
            setTokens({ token: sharedData.token, refreshToken: sharedData.refreshToken });
          }
          // Carry the freshly-stored access token onto the retry explicitly —
          // the request interceptor won't overwrite the (now-stale) header this
          // request already has from its first attempt.
          applyBearer(originalRequest);
          return api(originalRequest);
        } catch {
          // Refresh failed, fall through to redirect
        }
      }

      // Attempt token refresh. Send the refresh token in the body too (not just
      // the cookie) so refresh works on browsers that block the third-party
      // refresh cookie. withCredentials stays on so the cookie path is used
      // wherever it works.
      isRefreshing = true;
      refreshPromise = axios.post(
        `${API_URL}/api/refresh`,
        { refreshToken: getRefreshToken() || undefined },
        { withCredentials: true }
      );

      try {
        const refreshResponse = await refreshPromise;
        // Persist the rotated/returned tokens for the Bearer path. (No-ops on
        // the cookie path, where the body carries them too but the cookie is
        // already set.)
        const data = (refreshResponse && refreshResponse.data) || {};
        if (data.token || data.refreshToken) {
          setTokens({ token: data.token, refreshToken: data.refreshToken });
        }
        isRefreshing = false;
        refreshPromise = null;
        // Retry with the new token. The cookie (where it works) is also set
        // automatically by the refresh response.
        applyBearer(originalRequest);
        return api(originalRequest);
      } catch {
        // Refresh failed - token is fully expired. Drop the stale stored tokens
        // and fall through to the redirect-to-login handling below.
        isRefreshing = false;
        refreshPromise = null;
        clearTokens();
      }
    }

    // Handle 503 maintenance mode - show maintenance page
    if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
      if (!document.getElementById('maintenance-overlay')) {
        const overlay = document.createElement('div');
        overlay.id = 'maintenance-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:#16161A;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;';
        overlay.innerHTML = `<div style="text-align:center;max-width:500px;padding:40px 24px;">
          <div style="font-size:48px;margin-bottom:24px;">&#9874;</div>
          <h1 style="color:#6F9F95;font-size:28px;margin-bottom:16px;">Scheduled Maintenance</h1>
          <div style="width:60px;height:3px;background:#6F9F95;margin:24px auto;border-radius:2px;"></div>
          <p style="color:#A0A0A4;font-size:16px;line-height:1.6;margin-bottom:12px;">MedNecessity is currently undergoing a <span style="color:#fff;font-weight:600;">platform upgrade</span>.</p>
          <p style="color:#A0A0A4;font-size:16px;line-height:1.6;">We will be back online <span style="color:#fff;font-weight:600;">Monday morning</span>.</p>
          <p style="color:#A0A0A4;font-size:14px;margin-top:24px;">If you need immediate assistance, please contact your account representative.</p>
        </div>`;
        document.body.appendChild(overlay);
      }
      return Promise.reject(error);
    }

    // Handle 401 that can't be refreshed - redirect to login.
    // Skip /api/users/me: it's a session probe used by UserContext on every
    // app boot. A 401 there means "not logged in," which is informational —
    // UserContext sets user=null, and PrivateRoute handles redirecting
    // protected routes via <Navigate to="/login" />. The global redirect here
    // would otherwise kick unauthed users off public pages like NotFoundPage.
    if (
      error.response?.status === 401 &&
      !isRedirecting &&
      !originalRequest.url?.includes('/api/users/me')
    ) {
      isRedirecting = true;
      localStorage.removeItem('user');
      clearTokens();

      setTimeout(() => {
        if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
          window.location.href = '/login';
        }
        isRedirecting = false;
      }, 500);
    }

    return Promise.reject(error);
  }
);

export default api;
